import { useDispatch, useSelector } from 'react-redux';
import styles from './styles.module.scss';
import { getStateAndDataFlag } from 'store/selectors/clientsList';
import { READY } from 'settings/constants/apiState';
import { NoSearchResults } from 'components/Icons';
import { getConnectionTypeSelector } from 'store/selectors/app';
import { AGENT_CONNECTIONS } from 'settings/constants/drawers';
import { Button } from 'components';
import { getAgentTeamRoleSelector } from 'store/selectors/agentTeamDetail';
import { TEAM_OWNER, TEAM_ADMIN } from 'settings/constants/roles';
import { appSetConnectionTypeEffect } from 'store/effects';
import { useEmailVerificationWall } from 'features/emailVerification/useEmailVerificationWall';
import { ActionToInterrupt } from 'features/emailVerification/constants';
import { useCallback } from 'react';
import { getUserDataSelector } from 'store/selectors/user';
import {
  appManageClientDrawerAction,
  appSetClientDrawerActiveTab,
  appSetClientsSearchValueAction,
  appSetShowInviteClientAction,
  appSetShowInviteTeamMemberAction,
} from 'store/actions/app';

type NoConnectionsPropsType = {
  hasSearchQuery: string;
  hasResults: boolean;
  tabLabel: string;
};

const NoConnections = (props: NoConnectionsPropsType) => {
  const { hasSearchQuery, hasResults, tabLabel } = props;
  const dispatch = useDispatch();
  const user = useSelector(getUserDataSelector);
  const { state, isData } = useSelector(getStateAndDataFlag);
  const connectionType = useSelector(getConnectionTypeSelector);
  const buttonText = connectionType === AGENT_CONNECTIONS.Team ? 'Member' : 'Client';
  const agentRole = useSelector(getAgentTeamRoleSelector);
  const connectionTypeText =
    connectionType === AGENT_CONNECTIONS.Team ? 'team members' : connectionType.toLowerCase();

  const skeleton = () => (
    <div className={styles.skeleton}>
      <div className={styles.left}>
        <div className={styles.top}>
          <div className={styles.circle}></div>
          <div className={styles.bar}></div>
        </div>
        <div className={styles.bottom}></div>
      </div>
      <div className={styles.right}>
        <div className={styles.box}></div>
        <div className={styles.box}></div>
        <div className={styles.box}></div>
      </div>
    </div>
  );

  const inviteClientOrRequireEmailVerification = useEmailVerificationWall(
    ActionToInterrupt.INVITE_CLIENT,
    (state) => {
      dispatch(appSetShowInviteClientAction(state));
    },
  );

  const inviteTeamMemberOrRequireEmailVerification = useEmailVerificationWall(
    ActionToInterrupt.INVITE_TEAM_MEMBER,
    (state) => {
      dispatch(appSetShowInviteTeamMemberAction(state));
    },
  );

  const setShowInviteConnection = useCallback(
    (state, type) => {
      type === AGENT_CONNECTIONS.Clients
        ? inviteClientOrRequireEmailVerification(state)
        : inviteTeamMemberOrRequireEmailVerification(state);
    },
    [dispatch, user?.IsEmailVerified],
  );

  const inviteHandler = () => {
    dispatch(appManageClientDrawerAction(false));
    dispatch(appSetClientsSearchValueAction(''));
    setShowInviteConnection(true, connectionType);
  };

  if (state !== READY || !isData) return null;
  else if (hasSearchQuery && !hasResults)
    return (
      <div className={styles.noResults}>
        <NoSearchResults />
        <div>No results.</div>
      </div>
    );
  else if (!hasSearchQuery && !hasResults)
    return (
      <div className={styles.noConnections}>
        <div className={styles.skeletons}>
          {skeleton()}
          {skeleton()}
          {skeleton()}
        </div>
        <div
          className={styles.text}
        >{`You don’t have any ${tabLabel.toLowerCase()} ${connectionTypeText}.`}</div>
        {connectionType !== AGENT_CONNECTIONS.Collaborators &&
        [TEAM_OWNER, TEAM_ADMIN].includes(agentRole) ? (
          <Button
            testid="inviteVendor"
            className={styles.inviteButton}
            title={`Invite ${buttonText}`}
            onClick={inviteHandler}
          />
        ) : null}
      </div>
    );
  else return null;
};

export default NoConnections;
